import { useScopedAtomValue } from '@lemon8/web-app-shared/ssr';
import { $CurrentVideoArticle } from '@article/body/main/article-video/atoms';
import { FC, useCallback } from 'react';
import { useAtomValue } from 'jotai';
import { Link } from '@remix-run/react';
import { Avatar } from '~/components/avatar';
import useReflowClick from '~/utils/use-reflow-click';
import { useActiveToCurrentArticle } from '@article/shared/utils/use-active-to-current-article';
import { ClickPosition } from '~/utils/click-position';
import { UserAuthIcon } from '~/components/user-auth-icon';
import { useViewTracking } from '~/utils/use-view-tracking';
import { ModuleType } from '~/utils/module-type';
import { Button, ButtonSize } from '@lemon8/design';
import { useTranslation } from 'react-i18next';
import { $CurrentArticleAsAvailable } from '@article/shared/atoms/current-article';
import { useSEOHref } from '~/utils/build-seo-url';
import { ArticleDetailUtil } from 'shared/models';

import './style.scss';

export const VideoAuthor: FC = () => {
  const { author } = useScopedAtomValue($CurrentVideoArticle);
  const { t } = useTranslation();

  const article = useAtomValue($CurrentArticleAsAvailable);

  const activeToCurrentArticle = useActiveToCurrentArticle();

  const userHref = useSEOHref(ArticleDetailUtil.getAuthorHref(article));

  const videoAuthorObservedRef = useViewTracking({
    onView: useCallback(() => {
      window.Tea('wap_cta_show', {
        current_page_module: ModuleType.main_content,
        current_position: ClickPosition.main_author,
      });
      window.Tea('wap_cta_show', {
        current_page_module: ModuleType.main_content,
        current_position: ClickPosition.main_author_follow_btn,
      });
    }, []),
  });

  const handleAuthorInfoClick = useReflowClick(
    {
      clickEvent: () => ({
        current_position: ClickPosition.main_author,
        page_click_id: author.userId,
      }),
      reflowBehavior: () => activeToCurrentArticle(ClickPosition.main_author, author.userId),
    },
    [],
  );

  const handleButtonClick = useReflowClick(
    {
      clickEvent: () => ({
        current_position: ClickPosition.main_author_follow_btn,
        page_click_id: author.userId,
      }),
      reflowBehavior: () => activeToCurrentArticle(ClickPosition.main_author_follow_btn, author.userId),
      defaultBehavior: () => activeToCurrentArticle(ClickPosition.main_author_follow_btn, author.userId),
    },
    [],
  );

  return (
    <div className="video-author" ref={videoAuthorObservedRef}>
      <Link className="video-author-avatar" to={userHref} onClick={handleAuthorInfoClick}>
        <Avatar src={author.avatar} alt={author.nickName} />
      </Link>
      <div className="info">
        <Link to={userHref} className="name" onClick={handleAuthorInfoClick}>
          {author.nickName || '-'}
        </Link>
        <UserAuthIcon width={16} height={16} marginLeft={4} authInfo={author.authInfo} light={true} />
      </div>
      <Button variant="Secondary" size={ButtonSize.small} title={t('follow')} onClick={handleButtonClick} />
    </div>
  );
};
